import { useState, useCallback, useEffect, useRef, useMemo, type ReactNode } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useAuth } from '@common/hooks/useAuth'
import { useAbility, RequireAbility } from '@common/abilities'
import { useAppConfig } from '@common/config/AppConfigContext'
import { useNotificationPolling } from '@common/hooks/useNotificationPolling'
import { pubSubBroker } from '@common/pubSub'
import { useSessionTimeout } from '@common/hooks/useSessionTimeout'
import { usePageTracking } from '@common/hooks/usePageTracking'
import { usePageTitle } from '@common/hooks/usePageTitle'
import { useNotifications } from '@common/notifications/NotificationContext'
import { telemetry } from '@common/services/telemetry'
import { errorReporting } from '@common/services/errorReporting'
import { apiClient, setReadOnlyOptOutWindow } from '@common/api'
import { fraudQueuesApi, type FraudQueue } from '@common/api/services/fraudQueues'
import { userReportApi } from '@common/api/services/userReport'
import { AboutModal } from '@common/components/AboutModal'
import { SupportModal } from '@common/components/SupportModal'
import { SessionWarningModal } from '@common/components/SessionWarningModal'

/**
 * AppLayout — pixel-identical migration of AngularJS app.tpl.html.
 *
 * Preserves the exact DOM structure and CSS classes from the original:
 *   <div id="app-body">
 *     <header id="global-header" class="dispute-fraud">
 *     <div id="application-work-area">
 *       <nav id="global-navigation">
 *       <main id="page-content-container">
 *     <div class="content-mask">
 *
 * Bootstrap 3 + UX library classes are used directly to maintain
 * pixel-perfect rendering.
 */

interface AppLayoutProps {
    children: ReactNode
}

/** Auth routes that render WITHOUT the AppLayout shell (matching legacy top-level states) */
const AUTH_ROUTES = ['/login', '/callback', '/logout/callback', '/loginCallback', '/loginSilentCallback']

export function AppLayout({ children }: AppLayoutProps) {
    const { user, logout, isAuthenticated } = useAuth()
    const { hasAbility, userProfile, isProfileLoaded } = useAbility()
    const { config } = useAppConfig()
    const location = useLocation()

    // Start notification polling (matches AngularJS disputeNotificationsService in app.js)
    useNotificationPolling()

    // Subscribe to notification count events from pubSubBroker
    const [reviewRequestsCount, setReviewRequestsCount] = useState(0)
    const [pendingChargeoffsCount, setPendingChargeoffsCount] = useState(0)
    const [totalNotificationsCount, setTotalNotificationsCount] = useState(0)

    useEffect(() => {
        const subs = [
            pubSubBroker.subscribe('ReviewRequests:UpdateCount', (count: number) => setReviewRequestsCount(count)),
            pubSubBroker.subscribe('PendingChargeoff:UpdateCount', (count: number) => setPendingChargeoffsCount(count)),
            pubSubBroker.subscribe('TotalNotifications:UpdateCount', (count: number) => setTotalNotificationsCount(count)),
        ]
        return () => subs.forEach(s => s.unsubscribe())
    }, [])

    // Session timeout tracking (matches AngularJS angular-presence + session.js)
    const { showWarning, timeDisplay, onContinue, onLogout } = useSessionTimeout(isAuthenticated)

    // Automatic page view tracking (matches AngularJS $locationChangeSuccess handler)
    usePageTracking()

    // Update document.title on route changes (matches AngularJS $stateChangeSuccess title handling)
    usePageTitle()

    // Initialize telemetry + error reporting + read-only opt-out from app config (once)
    const servicesInitialized = useRef(false)
    useEffect(() => {
        if (config && !servicesInitialized.current) {
            if (config.telemetryKey) telemetry.init(config.telemetryKey)
            if (config.errorReportingKey) errorReporting.init(config.errorReportingKey)
            if (config.readOnlyDbConnectionOptOutWindowInMilliseconds) {
                setReadOnlyOptOutWindow(config.readOnlyDbConnectionOptOutWindowInMilliseconds)
            }
            servicesInitialized.current = true
        }
    }, [config])

    // Set/clear user context for telemetry + error reporting when profile changes
    const prevProfileRef = useRef<typeof userProfile>(null)
    useEffect(() => {
        if (isProfileLoaded && userProfile && !prevProfileRef.current) {
            telemetry.setContext(userProfile)
            errorReporting.setContext(userProfile)
        } else if (prevProfileRef.current && !userProfile) {
            telemetry.clearContext()
            errorReporting.clearContext()
        }
        prevProfileRef.current = userProfile
    }, [userProfile, isProfileLoaded])

    // ALL hooks must be called before any conditional return (React Rules of Hooks).
    // Dropdown open states — mirrors AngularJS $scope vars
    const [headerddmoreopen, setHeaderddmoreopen] = useState(false)
    const [headerddcustomeropen, setHeaderddcustomeropen] = useState(false)
    const [headerddaccountopen, setHeaderddaccountopen] = useState(false)
    const [isAboutOpen, setIsAboutOpen] = useState(false)
    const [isSupportOpen, setIsSupportOpen] = useState(false)

    // Dynamic fraud queue navigation (matches AngularJS fraudQueuesApi.get() in app.js)
    const [fraudAlertQueues, setFraudAlertQueues] = useState<FraudQueue[]>([])
    const [fraudCaseQueues, setFraudCaseQueues] = useState<FraudQueue[]>([])

    useEffect(() => {
        if (!isAuthenticated || !hasAbility('ViewFraudAlertOrCaseQueues')) return

        fraudQueuesApi.get().then((queues) => {
            if (hasAbility('ViewFraudAlertQueues')) {
                setFraudAlertQueues(queues.filter((q) => !q.isFraudCaseQueue))
            }
            if (hasAbility('ViewFraudCase')) {
                setFraudCaseQueues(queues.filter((q) => q.isFraudCaseQueue))
            }
        }).catch(() => {
            // Silently fail — matching AngularJS behavior where promise rejection
            // was unhandled and queues simply remained empty
        })
    }, [isAuthenticated, hasAbility])

    // Dynamic analytics reports navigation (matches AngularJS getUserReports() in app.js)
    interface AnalyticsReport {
        reportId?: string | null
        reportName: string
    }
    const [analyticsReports, setAnalyticsReports] = useState<AnalyticsReport[]>([])

    useEffect(() => {
        if (!isAuthenticated || !hasAbility('ClientViewReports')) return

        userReportApi.getAll()
            .then((results) => {
                setAnalyticsReports((results as AnalyticsReport[]) || [])
            })
            .catch(() => {
                // Silently fail — matching AngularJS behavior where getUserReports
                // caught errors via handleError and reports remained null
            })
    }, [isAuthenticated, hasAbility])

    const { success: showSuccess, error: showError } = useNotifications()

    const handleSupportSubmit = useCallback(async (data: { subject: string; date: string; description: string }) => {
        try {
            await apiClient.post('/support/request', data)
            showSuccess('Support request submitted successfully.')
        } catch (err: unknown) {
            showError(err instanceof Error ? err.message : 'Failed to submit support request')
        }
    }, [showSuccess, showError])

    const currentYear = new Date().getFullYear()

    const userInitials = useMemo(() => {
        if (!userProfile?.firstName || !userProfile?.lastName) return '??'
        return userProfile.firstName[0] + userProfile.lastName[0]
    }, [userProfile])

    const handleLogout = useCallback(() => {
        logout()
    }, [logout])

    /** Returns true if current hash path starts with the given prefix */
    const isActiveState = useCallback(
        (prefix: string) => {
            return location.pathname.startsWith(prefix)
        },
        [location.pathname],
    )

    const anyDropdownOpen =
        headerddmoreopen || headerddcustomeropen || headerddaccountopen

    // Legacy pattern: login/callback states are top-level (NOT children of 'main'),
    // so they render without the header/navigation shell.
    // This conditional return is AFTER all hooks to satisfy React Rules of Hooks.
    const isAuthRoute = AUTH_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + '/'))
    if (isAuthRoute) {
        return <>{children}</>
    }

    return (
        <div id="app-body">
            {/* ========== Global Header ========== */}
            <header id="global-header" className="dispute-fraud">
                <div className="header-col header-col-a">
                    {/* App info dropdown */}
                    <div
                        className={`drs-dropdown dropdown-lg drs-dropdown-left${headerddmoreopen ? ' open' : ''}`}
                        id="global-app-info"
                    >
                        <div
                            className={`btn btn-default btn-round btn-round-gray btn-round-more_vert drs-dropdown-trigger${headerddmoreopen ? ' btn-default-active' : ''}`}
                            role="button"
                            onClick={() => setHeaderddmoreopen(!headerddmoreopen)}
                        />
                        {headerddmoreopen && (
                            <div className="dropdown-content">
                                <div className="dropdown-bd">
                                    <div id="logo-moreinfo-adsol" />
                                    <p className="text-xs">
                                        <strong>Version 1.0.0</strong>
                                    </p>
                                    <p className="text-xs">
                                        This is a licensed copy of Advantage Solutions, a
                                        product of Advantage Payment Services.
                                    </p>
                                    <p>
                                        <a
                                            href="http://advantagepaymentservices.com/"
                                            className="btn btn-default btn-sm"
                                            target="new"
                                        >
                                            <i className="icon-link" />
                                            <span>Visit APS Website</span>
                                        </a>
                                    </p>
                                </div>
                                <div className="dropdown-bd">
                                    <div id="logo-moreinfo-aps" />
                                    <p className="text-xs">
                                        &copy; {currentYear} Advantage Payment Services
                                    </p>
                                    <p>
                                        <a
                                            href="#"
                                            className="btn btn-default btn-sm"
                                            onClick={(e) => { e.preventDefault(); setIsAboutOpen(true); setHeaderddmoreopen(false) }}
                                        >
                                            <span>About</span>
                                        </a>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* APS Logo */}
                    <div id="aps-logo-hd" />
                </div>

                <div className="header-col header-col-b">
                    {/* Customer dropdown */}
                    <RequireAbility ability="ViewCustomerDropdown">
                        <div
                            className={`drs-dropdown${headerddcustomeropen ? ' open' : ''}`}
                            id="global-app-options"
                        >
                            <div
                                className={`btn btn-default btn-round btn-round-gray btn-round-customers dropdown-trigger${headerddcustomeropen ? ' btn-default-active' : ''}`}
                                role="button"
                                onClick={() =>
                                    setHeaderddcustomeropen(!headerddcustomeropen)
                                }
                            />
                            {headerddcustomeropen && (
                                <div className="dropdown-content">
                                    <div className="menu">
                                        <div className="menu-hd">Customers</div>
                                        <div className="list list-small list-lines">
                                            <ul>
                                                <RequireAbility ability="ViewClearedCustomers">
                                                    <li className="nav-option">
                                                        <a href="/#/cleared-customers">
                                                            Cleared Customers
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </RequireAbility>

                    {/* Support button */}
                    <RequireAbility ability="SubmitSupportRequest">
                        <div className="drs-dropdown" id="global-app-options">
                            <a
                                role="button"
                                className="btn btn-default btn-round btn-round-gray btn-round-support dropdown-trigger"
                                onClick={(e) => { e.preventDefault(); setIsSupportOpen(true) }}
                            />
                        </div>
                    </RequireAbility>

                    {/* Settings button */}
                    <RequireAbility ability="ViewAdminDropdown">
                        <div className="drs-dropdown" id="global-app-options">
                            <a
                                role="button"
                                className="btn btn-default btn-round btn-round-gray btn-round-settings dropdown-trigger"
                                href="/#/settings"
                            />
                        </div>
                    </RequireAbility>

                    {/* User account dropdown */}
                    <div
                        className={`drs-dropdown dropdown-standard${headerddaccountopen ? ' open' : ''}`}
                        id="global-user-options"
                    >
                        <div
                            className={`stamp stamp-color-gray dropdown-trigger${headerddaccountopen ? ' stamp-active' : ''}`}
                            onClick={() =>
                                setHeaderddaccountopen(!headerddaccountopen)
                            }
                        >
                            <div className="stamp-txt">{userInitials}</div>
                        </div>
                        {headerddaccountopen && (
                            <div className="dropdown-content">
                                <div className="dropdown-bd">
                                    <div className="content-group content-group-tighter">
                                        <div className="title-group">
                                            <p>
                                                <strong>
                                                    {userProfile?.firstName} {userProfile?.lastName}
                                                </strong>
                                            </p>
                                        </div>
                                        {userProfile?.previousLoginDate && (
                                            <div className="timestamp timestamp-default">
                                                <div className="timestamp-label">
                                                    Last Login:
                                                </div>
                                                <div className="timestamp-datetime">
                                                    {userProfile.previousLoginDate}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="menu">
                                    <div className="list list-small list-lines list-top-border">
                                        <ul>
                                            <RequireAbility ability="UpdateAuthenticationSettings">
                                                <li className="nav-option">
                                                    <a href="/#/authentication-settings">
                                                        Authentication Settings
                                                    </a>
                                                </li>
                                            </RequireAbility>
                                        </ul>
                                    </div>
                                </div>
                                <div className="dropdown-ft">
                                    <a
                                        href="#"
                                        className="btn btn-primary btn-block"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            handleLogout()
                                        }}
                                    >
                                        Sign Out
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* ========== Application Work Area ========== */}
            <div
                id="application-work-area"
                className={anyDropdownOpen ? 'application-work-area-blur' : ''}
            >
                {/* ========== Global Navigation ========== */}
                <nav id="global-navigation" role="navigation">
                    <ul className="list list-global-nav" id="primary-navigation">
                        {/* Dashboard */}
                        {(hasAbility('ViewDashboard') ||
                            (userProfile?.disputeCaseDataSource === 'API' && hasAbility('ViewDisputeTeamPerformance'))) && (
                            <li
                                className={`list-global-option list-global-option-dashboard${isActiveState('/dashboard') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Dashboard</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Dashboards
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <RequireAbility ability="ViewDashboard">
                                                    <li className="list-option">
                                                        <a href="/#/dashboard/disputes">
                                                            Disputes Dashboard
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                {userProfile?.disputeCaseDataSource === 'API' && (
                                                    <RequireAbility ability="ViewDisputeTeamPerformance">
                                                        <li className="list-option">
                                                            <a href="/#/dashboard/disputeTeamPerformance">
                                                                Team Performance Dashboard
                                                            </a>
                                                        </li>
                                                    </RequireAbility>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )}

                        {/* Case Management */}
                        <RequireAbility ability="ViewCases">
                            <li
                                className={`list-global-option list-global-option-cases${isActiveState('/disputes') || isActiveState('/transactions') || isActiveState('/arm-cases') || isActiveState('/watchlistCases') || isActiveState('/bsaReview') || isActiveState('/irfReview') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Case Management</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Case Management
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <RequireAbility
                                                    allOf={['RegZ', 'ViewDisputes']}
                                                >
                                                    <li className="list-option-label">
                                                        Credit (Reg Z)
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility
                                                    allOf={[
                                                        'RegZ',
                                                        'ViewMyDisputeCases',
                                                    ]}
                                                >
                                                    <li className="list-option">
                                                        <a href="/#/disputes/list/credit/mycases">
                                                            My Cases
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility
                                                    allOf={['RegZ', 'ViewDisputes']}
                                                >
                                                    <li className="list-option">
                                                        <a href="/#/disputes/list/credit">
                                                            Open Cases
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility
                                                    allOf={[
                                                        'RegZ',
                                                        'ViewUnassignedDisputes',
                                                    ]}
                                                >
                                                    <li className="list-option">
                                                        <Link to="/disputes/list/credit" state={{ type: 'Closed' }}>
                                                            Closed Cases
                                                        </Link>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewDisputes">
                                                    <li className="list-option-label">
                                                        Debit (Reg E)
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewMyDisputeCases">
                                                    <li className="list-option">
                                                        <a href="/#/disputes/list/debit/mycases">
                                                            My Cases
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewDisputes">
                                                    <li className="list-option">
                                                        <a href="/#/disputes/list/debit">
                                                            Open Cases
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewUnassignedDisputes">
                                                    <li className="list-option">
                                                        <Link to="/disputes/list/debit" state={{ type: 'Closed' }}>
                                                            Closed Cases
                                                        </Link>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewProvisionalCreditCases">
                                                    <li className="list-option">
                                                        <a href="/#/disputes/casesprovisionalcredit">
                                                            Pending PC (Cases)
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewTransactions">
                                                    <li className="list-option">
                                                        <a href="/#/transactions?type=1">
                                                            Pending PC (Transactions)
                                                        </a>
                                                    </li>
                                                    <li className="list-option">
                                                        <a href="/#/transactions?type=2">
                                                            Aging ATM Transactions
                                                        </a>
                                                    </li>
                                                    <li className="list-option">
                                                        <a href="/#/transactions?type=3">
                                                            Aging POS Transactions
                                                        </a>
                                                    </li>
                                                    <li className="list-option">
                                                        <a href="/#/transactions?type=4">
                                                            Aging 120 Transactions
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="Watchlist-ManageCase">
                                                    <li className="list-option-label">
                                                        Watchlist
                                                    </li>
                                                    <li className="list-option">
                                                        <a href="/#/watchlistCases?type=Active">
                                                            Active
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="Watchlist-ViewClosedCase">
                                                    <li className="list-option">
                                                        <a href="/#/watchlistCases?type=Closed">
                                                            Closed
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                {(hasAbility('ViewMastercomRequestLog') ||
                                                    (userProfile?.clientIdentificationNumberPreference === 'BIN' && hasAbility('ViewProcessorApiLog'))) && (
                                                    <li className="list-option-label">
                                                        Monitoring
                                                    </li>
                                                )}
                                                {userProfile?.clientIdentificationNumberPreference === 'BIN' && (
                                                    <RequireAbility ability="ViewProcessorApiLog">
                                                        <li className="list-option">
                                                            <a href="/#/disputes/processor-api-log">
                                                                Processor Api Log
                                                            </a>
                                                        </li>
                                                    </RequireAbility>
                                                )}
                                                <RequireAbility ability="ViewMastercomRequestLog">
                                                    <li className="list-option">
                                                        <a href="/#/disputes/mastercom-request-log">
                                                            Mastercom Request Log
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewFraudCase">
                                                    <li className="list-option list-option-label">
                                                        Advantage Risk Monitor
                                                    </li>
                                                    {fraudCaseQueues[0] && (
                                                        <li key={fraudCaseQueues[0].id} className="list-option">
                                                            <a href={`/#/arm-cases/${fraudCaseQueues[0].id}`}>
                                                                {fraudCaseQueues[0].name}
                                                            </a>
                                                        </li>
                                                    )}
                                                    <li className="list-option">
                                                        <a href="/#/sar-filed">
                                                            SAR Filed
                                                        </a>
                                                    </li>
                                                    {fraudCaseQueues.slice(1).map((queue) => (
                                                        <li key={queue.id} className="list-option">
                                                            <a href={`/#/arm-cases/${queue.id}`}>
                                                                {queue.name}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </RequireAbility>
                                                <RequireAbility ability="ManageFraudAlertIRF">
                                                    <li className="list-option-label">
                                                        ARM Review
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ApproveBSAClosure">
                                                    <li className="list-option">
                                                        <a href="/#/bsaReview">
                                                            BSA Reviews
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ManageFraudAlertIRF">
                                                    <li className="list-option">
                                                        <a href="/#/irfReview">
                                                            IRF Reviews
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ManageSARForms">
                                                    <li className="list-option">
                                                        <a href="/#/sarReviews/pending">
                                                            SAR Reviews
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* Call Center */}
                        <RequireAbility ability="CSRCreateCase">
                            <li
                                className={`list-global-option list-global-option-inquiries${isActiveState('/inquiries') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Call Center</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <li className="list-option-label">
                                                    Call Center
                                                </li>
                                                <li className="list-option">
                                                    <a href="/#/inquiries/new">Intake</a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* Documents */}
                        <RequireAbility ability="ViewDocManagementOption">
                            <li
                                className={`list-global-option list-global-option-documents${isActiveState('/docmgmt') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Documents</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Documents
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <RequireAbility ability="ViewDocumentManagementQueues">
                                                    <li className="list-option">
                                                        <a href="/#/docmgmt/queues">
                                                            Documents &amp; Queues
                                                        </a>
                                                    </li>
                                                    <li className="list-option">
                                                        <a href="/#/docmgmt/uploadDocuments">
                                                            Upload Files
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewDocumentManagementSharedDocuments">
                                                    <li className="list-option">
                                                        <a href="/#/docmgmt/shared-documents">
                                                            Shared Documents
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* Cards (Comp Cards) */}
                        <RequireAbility ability="ViewCompCards">
                            <li
                                className={`list-global-option list-global-option-fraud${isActiveState('/comp-cards') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Cards</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Cards
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <RequireAbility ability="ViewCompCardAlerts">
                                                    <li className="list-option">
                                                        <a href="/#/comp-cards/alerts">
                                                            Compromised Card Alerts
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* ARM Cases (Alert Queues) — dynamically loaded from fraudQueuesApi */}
                        <RequireAbility ability="ViewFraudAlertQueues">
                            <li
                                className={`list-global-option list-global-option-alerts${isActiveState('/arm-alerts') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>ARM Cases</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            ARM Alerts
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <li className="list-option list-option-label">
                                                    Advantage Risk Monitor Queues
                                                </li>
                                                {fraudAlertQueues.map((queue) => (
                                                    <li key={queue.id} className="list-option">
                                                        <a href={`/#/arm-alerts/${queue.id}`}>
                                                            {queue.name}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* Reviews */}
                        <RequireAbility ability="ViewReviews">
                            <li
                                className={`list-global-option list-global-option-escalations${isActiveState('/reviews') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Reviews</span>
                                    {totalNotificationsCount > 0 && (
                                        <>{' '}<div className="notification-badge">{totalNotificationsCount}</div></>
                                    )}
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Reviews
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <RequireAbility ability="ViewReviewRequests">
                                                    <li className="list-option">
                                                        <a href="/#/reviews/status" className={reviewRequestsCount > 0 ? 'has-indicator' : ''}>
                                                            <span className="list-option-text">
                                                                Review Requests
                                                            </span>
                                                            {reviewRequestsCount > 0 && (
                                                                <>{' '}<div className="notification-badge">{reviewRequestsCount}</div></>
                                                            )}
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewPendingChargeoffs">
                                                    <li className="list-option">
                                                        <a href="/#/reviews/write-off" className={pendingChargeoffsCount > 0 ? 'has-indicator' : ''}>
                                                            <span className="list-option-text">
                                                                Pending Writeoffs
                                                            </span>
                                                            {pendingChargeoffsCount > 0 && (
                                                                <>{' '}<div className="notification-badge">{pendingChargeoffsCount}</div></>
                                                            )}
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewActivityNotificationsDashboard">
                                                    <li className="list-option">
                                                        <a href="/#/disputes/case-activity">
                                                            <span className="list-option-text">
                                                                Dispute Case Activity
                                                            </span>
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewVerificationRequests">
                                                    <li className="list-option">
                                                        <a href="/#/reviews/verification/flexId">
                                                            <span className="list-option-text">
                                                                Address Advantage
                                                            </span>
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* Research */}
                        <RequireAbility ability="ViewResearchDropdown">
                            <li
                                className={`list-global-option list-global-option-research${isActiveState('/lookup') || isActiveState('/results') || isActiveState('/dispute-trends') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Research</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Research
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <RequireAbility ability="ViewDisputeTrendTransactions">
                                                    <li className="list-option">
                                                        <a href="/#/dispute-trends/transactions">
                                                            Disputed Transaction Analysis
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="OfacLookup">
                                                    <li className="list-option">
                                                        <a href="/#/lookup">
                                                            Watchlist Search
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* Reporting */}
                        <RequireAbility ability="ViewReportingDropdown">
                            <li
                                className={`list-global-option list-global-option-reports${isActiveState('/reporting') || isActiveState('/batchArchive') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Reporting</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Reporting
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                <RequireAbility ability="GenerateCIPAnalysisReport">
                                                    <li className="list-option">
                                                        <a href="/#/reporting/cip-analysis">
                                                            CIP Analysis
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewCIPDefectReport">
                                                    <li className="list-option">
                                                        <a href="/#/reporting/cip-defect-report">
                                                            CIP Exception Rule Report
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="OfacLookup">
                                                    <li className="list-option">
                                                        <a href="/#/batchArchive">
                                                            Watchlist Batch Archive
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="ViewCustomRiskReport">
                                                    <li className="list-option">
                                                        <a href="/#/reporting/custom-risk-report">
                                                            Flagged Risk Code Analysis
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                                <RequireAbility ability="GenerateFraudReport">
                                                    <li className="list-option">
                                                        <a href="/#/reporting/arm-report">
                                                            Advantage Risk Monitor Alert
                                                            Report
                                                        </a>
                                                    </li>
                                                </RequireAbility>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>

                        {/* Analytics */}
                        <RequireAbility ability="ClientViewReports">
                            <li
                                className={`list-global-option list-global-option-powerbi${isActiveState('/analytics') ? ' list-global-option-active' : ''}`}
                            >
                                <a href="">
                                    <span>Analytics</span>
                                </a>
                                <div className="primary-nav-subnav-container">
                                    <div className="list-option-subnav">
                                        <div className="list-option-subnav-hd">
                                            Analytics
                                        </div>
                                        <div className="list-option-subnav-bd">
                                            <ul className="list list-submenu">
                                                {analyticsReports.map((report, idx) => (
                                                    <li key={report.reportId || `label-${idx}`} className={report.reportId ? 'list-option' : 'list-option-label'}>
                                                        {report.reportId ? (
                                                            <a href={`/#/analytics/reports/${report.reportId}`}>{report.reportName}</a>
                                                        ) : (
                                                            <span>{report.reportName}</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </RequireAbility>
                    </ul>
                </nav>

                {/* ========== Main Content ========== */}
                <main id="page-content-container" className="container-fluid">
                    {children}
                </main>
            </div>

            {/* ========== Content Mask (for dropdown blur) ========== */}
            <div
                className={`content-mask${anyDropdownOpen ? ' content-mask-open' : ''}`}
                onClick={() => {
                    setHeaderddmoreopen(false)
                    setHeaderddcustomeropen(false)
                    setHeaderddaccountopen(false)
                }}
            />

            {/* ========== App-Level Modals ========== */}
            <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
            <SupportModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} onSubmit={handleSupportSubmit} />
            <SessionWarningModal show={showWarning} timeDisplay={timeDisplay} onContinue={onContinue} onLogout={onLogout} />
        </div>
    )
}
